import { apiClient, HttpError } from '@littlebird/auth-client';
import {
  FeedbackDraftSchema,
  toSendFeedbackDraftBody,
  type SendFeedbackDraftInput,
} from '@littlebird/ui-utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export const feedbackReportKeys = {
  draft: (draftId: string) => ['draft', draftId] as const,
};

export function useFeedbackDraftQuery(draftId: string) {
  return useQuery({
    queryKey: feedbackReportKeys.draft(draftId),
    queryFn: async () => {
      const response = await apiClient.get(`feedback/drafts/${draftId}`);
      return FeedbackDraftSchema.parseAsync(response);
    },
    retry: (failureCount: number, error) => {
      if (error instanceof HttpError && error.status === 404) return false;
      return failureCount < 3;
    },
  });
}

export type SendFeedbackDraftArgs = SendFeedbackDraftInput & {
  draftId: string;
};

export function useSendFeedbackDraftMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ draftId, ...input }: SendFeedbackDraftArgs) => {
      await apiClient.post(
        `feedback/drafts/${draftId}/send`,
        toSendFeedbackDraftBody(input)
      );
    },
    onSuccess: (_, { draftId }) => {
      queryClient.invalidateQueries({
        queryKey: feedbackReportKeys.draft(draftId),
      });
    },
  });
}
